import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { useModal } from '~/hooks/useModal';
import { cartStore, checkoutStore, mainStore, orderStore } from '~/stores';
import { PICKUP_ALCOHOL_COUPON_CODE } from '~/stores/constants';
import { ApplyPromocodeCallerSource } from '~/stores/PromotionsStore/interfaces';

import Icon from '../Icon/Icon';

export default observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [promocodeVal, setPromocodeVal] = useState(
    cartStore.promocode.value || '',
  );
  const [warningMsg, setWarningMsg] = useState('');

  const isLoading = cartStore.promotionStore.applyPromocodeMeta.isLoading;

  const backHandler = () => closeModal();

  const handlePromocodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPromocodeVal(e.currentTarget.value.trim());
  };

  const handleDelPromocode = () => {
    setPromocodeVal('');
    cartStore.promotionStore.resetPromocode();
  };

  const handleSubmit = () => {
    checkPromocode(promocodeVal);
  };

  const checkPromocode = (val: string) => {
    if (!val.length) {
      return;
    }

    cartStore.promotionStore
      .applyPromocode(val, ApplyPromocodeCallerSource.CHECKOUT)
      .then(() => {
        if (cartStore.promocode.deliveryMethod) {
          checkoutStore.setDeliveryMethod(cartStore.promocode.deliveryMethod);
        }
        if (cartStore.promocode.success) {
          backHandler();
        }
      });
  };

  useEffect(() => {
    if (
      !cartStore.totalCartPrice.amountLeftToDiscount ||
      mainStore.isZero(cartStore.totalCartPrice.amountLeftToDiscount)
    ) {
      setWarningMsg('');
      return;
    }

    if (!cartStore.isPromoCodeApplied && cartStore.promocode.coupon?.type) {
      let msg =
        cartStore.promocode.coupon?.code === PICKUP_ALCOHOL_COUPON_CODE
          ? 'errors:promocodePriceLessNoAlcohol'
          : 'errors:promocodePriceLess';

      if (!company.isExceptionAlcohol) {
        msg = 'errors:promocodePriceLessNoAlcohol';
      }

      setWarningMsg(
        t(msg, {
          amountLeft: mainStore.addCurrencySymbol(
            cartStore.totalCartPrice.amountLeftToDiscount,
          ),
          discount: cartStore.formatPromocodeDiscountAmount,
        }),
      );
    } else {
      setWarningMsg('');
    }
  }, [cartStore.totalCartPrice]);

  useEffect(() => {
    if (!cartStore.promocode.value || !orderStore.etaCalculation) {
      return;
    }
    checkPromocode(cartStore.promocode.value);
  }, [JSON.stringify(orderStore.etaCalculation?.cost)]);

  return (
    <Modal
      isShow={true}
      className="modal-promocode__body"
      onBackdropDismiss={backHandler}
    >
      <div className="popover-container input-form">
        <div className="popover">
          <button
            className="button _no-padding _no-color input-form__close"
            onClick={backHandler}
          >
            <Icon type="plus" className="icon__rotate-45" size={36} />
          </button>
          <button
            className="button _no-padding _no-color input-form__back"
            onClick={backHandler}
          >
            <Icon type="arrow" className="icon__rotate-180" size={24} />
          </button>
          <div
            className={classes([
              'input-form__form',
              promocodeVal.length &&
                cartStore.promocode.success === null &&
                !isLoading &&
                '_filled',
              cartStore.promocode.errorType === 'error' &&
                !isLoading &&
                '_error',
              (cartStore.promocode.errorType === 'warning' || warningMsg) &&
                !isLoading &&
                '_warning',
              cartStore.promocode.success === true &&
                !warningMsg &&
                !isLoading &&
                '_success',
            ])}
          >
            <div className="input-form__form-header">
              <p className="input-form__form-header-title">
                {t('addPromocode')}
              </p>
              <div className="input-form__input-container">
                <input
                  placeholder={t('phrases:enterPromocode')}
                  value={promocodeVal}
                  onChange={handlePromocodeChange}
                  className="input-form__input-visible"
                  disabled={isLoading}
                />

                {cartStore.promocode.value.length > 0 && (
                  <button
                    className="button _no-color _no-padding"
                    onClick={handleDelPromocode}
                    disabled={isLoading}
                  >
                    <Icon type="plus" size={36} className="icon__rotate-45" />
                  </button>
                )}
              </div>

              {!isLoading && cartStore.promocode.message && (
                <span className="helper-text">
                  {cartStore.promocode.message}
                </span>
              )}
            </div>
            <div className="input-form__form-footer">
              <div className="input-form__form-footer-buttons">
                <button
                  type="button"
                  className="button _primary _med"
                  onClick={handleSubmit}
                  disabled={isLoading || promocodeVal.length === 0}
                >
                  {t('submitCode')}
                  {isLoading && <span className="spinner _small _white" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
});
